import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { doctorDashboardAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  Users, Stethoscope, CalendarDays, IndianRupee, Clock, Activity,
  ArrowUpRight, ArrowDownRight, Award, ChevronRight, X, UserCheck,
  Heart, AlertTriangle, BedDouble, FileText, Phone, Star, RefreshCw,
} from 'lucide-react';
import { cn } from '../lib/utils';
import DayCalendar from '../components/DayCalendar';
import AttendanceCalendar from '../components/AttendanceCalendar';
import WeeklyScheduleEditor from '../components/WeeklyScheduleEditor';

// ========== SPARKLINE ==========
const Sparkline = ({ data, color = '#10b981', h = 28 }) => {
  if (!data?.length) return null;
  const max = Math.max(...data, 1);
  const pts = data.map((v, i) => `${(i / Math.max(data.length - 1, 1)) * 100},${100 - (v / max) * 80}`).join(' ');
  return (
    <svg viewBox="0 0 100 100" className="w-full" style={{ height: h }} preserveAspectRatio="none">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

// ========== DONUT — color-coded, percent-labeled, animated ==========
const Donut = ({ data, size = 150 }) => {
  const total = data.reduce((s, d) => s + d.value, 0);
  const r = (size - 26) / 2;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  const visible = data.filter(d => d.value > 0);
  const single = visible.length === 1;
  return (
    <div className="flex items-center gap-5">
      <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
          {total === 0 && (
            <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#e2e8f0" strokeWidth="22" />
          )}
          {total > 0 && !single && (
            <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#f1f5f9" strokeWidth="22" />
          )}
          {single && (
            <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={visible[0].color} strokeWidth="22"
              className="transition-all duration-700" />
          )}
          {!single && visible.map((d, i) => {
            const pct = d.value / total;
            const dash = circ * pct;
            const co = offset;
            offset += pct;
            return (
              <circle key={i} cx={size/2} cy={size/2} r={r} fill="none" stroke={d.color}
                strokeWidth="22" strokeLinecap="butt"
                strokeDasharray={`${dash} ${circ - dash}`}
                strokeDashoffset={-circ * co}
                className="transition-all duration-700" />
            );
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <p className="text-2xl font-bold text-slate-800">{total}</p>
          <p className="text-[10px] uppercase text-slate-400 tracking-wider">Total</p>
        </div>
      </div>
      <div className="space-y-2">
        {data.map((d, i) => {
          const pct = total > 0 ? Math.round((d.value / total) * 100) : 0;
          return (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span className="h-3 w-3 rounded-sm" style={{ background: d.color }} />
              <span className="text-slate-500">{d.label}</span>
              <span className="font-bold text-slate-700">{d.value}</span>
              {total > 0 && <span className="text-[10px] text-slate-400">({pct}%)</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ========== BAR CHART ==========
const BarChart = ({ data, h = 140 }) => {
  if (!data?.length) return <p className="text-xs text-slate-400 text-center py-6">No data</p>;
  const max = Math.max(...data.map(d => d.total), 1);
  return (
    <div>
      <div className="flex items-end gap-1" style={{ height: h }}>
        {data.map((d, i) => (
          <div key={i} className="flex-1 flex flex-col gap-0.5 justify-end" style={{ height: h }}>
            <div className="w-full bg-emerald-500 rounded-t-sm transition-all duration-500 hover:bg-emerald-400"
              style={{ height: `${(d.completed / max) * 100}%`, minHeight: d.completed > 0 ? 2 : 0 }} />
            <div className="w-full bg-slate-300 rounded-t-sm"
              style={{ height: `${((d.total - d.completed) / max) * 100}%`, minHeight: (d.total - d.completed) > 0 ? 2 : 0 }} />
          </div>
        ))}
      </div>
      <div className="flex mt-1">
        {data.map((d, i) => <span key={i} className="flex-1 text-center text-[8px] text-slate-400">{d.month}</span>)}
      </div>
    </div>
  );
};

// ========== DOCTOR VIEW (Single Doctor) ==========
const DoctorSelfView = ({ data, canViewFinance = true, onRescheduled }) => {
  const { kpi, patient_summary, today_appointments, next_patient, monthly_trend, attendance, schedule, gender_distribution, age_groups } = data;
  // Item 6 — clickable status filter pill (Waiting / In Progress / Completed)
  const [statusFilter, setStatusFilter] = useState(null);

  const tCounts = (today_appointments || []).reduce((acc, a) => {
    if (a.status === 'completed') acc.completed += 1;
    else if (a.status === 'in_progress') acc.in_progress += 1;
    else if (a.status === 'cancelled' || a.status === 'no_show') acc.dropped += 1;
    else acc.waiting += 1; // scheduled + checked_in are waiting/in-queue from doctor's view
    return acc;
  }, { waiting: 0, in_progress: 0, completed: 0, dropped: 0 });

  const filteredAppointments = (today_appointments || []).filter(a => {
    if (!statusFilter) return true;
    if (statusFilter === 'waiting') return a.status === 'scheduled' || a.status === 'checked_in';
    if (statusFilter === 'in_progress') return a.status === 'in_progress';
    if (statusFilter === 'completed') return a.status === 'completed';
    return true;
  });

  // KPI cards — drop Month Revenue for non-finance viewers (e.g. Front Desk inspecting a doctor)
  const kpiCards = [
    { label: 'Total Patients', value: kpi.total_patients, sub: 'All time', icon: Users, iconBg: 'bg-blue-500', trend: kpi.patient_trend, change: kpi.patients_change, color: '#3b82f6' },
    { label: "Today's Patients", value: kpi.today_patients, sub: new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }), icon: UserCheck, iconBg: 'bg-emerald-500', trend: kpi.patient_trend, change: null, color: '#10b981' },
    { label: "Today's Appointments", value: kpi.today_appointments, sub: `${kpi.today_completed} done, ${kpi.today_pending} pending`, icon: CalendarDays, iconBg: 'bg-indigo-500', trend: kpi.appointment_trend, change: kpi.appointments_change, color: '#6366f1' },
    ...(canViewFinance ? [{ label: 'Month Revenue', value: kpi.month_revenue, sub: patient_summary.month, icon: IndianRupee, iconBg: 'bg-amber-500', prefix: 'Rs.', trend: null, change: null, color: '#f59e0b' }] : []),
  ];

  return (
    <div className="space-y-6" data-testid="doctor-self-dashboard">
      {/* Doctor Header */}
      <div className="flex items-center gap-4">
        <div className="h-14 w-14 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xl font-bold shadow-lg">
          {data.name?.charAt(0) || 'D'}
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{data.name}</h1>
          <p className="text-sm text-slate-500">{data.department} {data.specialization && data.specialization !== data.department ? `- ${data.specialization}` : ''}</p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className={cn("grid grid-cols-2 gap-4", canViewFinance ? "lg:grid-cols-4" : "lg:grid-cols-3")}>
        {kpiCards.map((card, i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg transition-all duration-300" data-testid={`kpi-${card.label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
            <div className="flex items-start justify-between mb-2">
              <div className={cn("h-10 w-10 rounded-xl flex items-center justify-center shadow-sm", card.iconBg)}>
                <card.icon size={20} className="text-white" />
              </div>
              {card.change !== null && card.change !== undefined && (
                <span className={cn("text-[11px] font-bold px-2 py-0.5 rounded-full flex items-center gap-0.5",
                  card.change >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"
                )}>
                  {card.change >= 0 ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}
                  {card.change >= 0 ? '+' : ''}{card.change}%
                </span>
              )}
            </div>
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">{card.label}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{card.prefix || ''}{typeof card.value === 'number' ? card.value.toLocaleString('en-IN') : card.value}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">{card.sub}</p>
            {card.trend && <div className="mt-2 opacity-60"><Sparkline data={card.trend} color={card.color} /></div>}
          </div>
        ))}
      </div>

      {/* Main Grid: Patient Summary + Today's Appointments + Next Patient */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Patient Summary Donut */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Patient Summary {patient_summary.month} {patient_summary.year}</h3>
          <Donut data={[
            { label: 'New Patients', value: patient_summary.new, color: '#3b82f6' },
            { label: 'Returning', value: patient_summary.returning, color: '#f59e0b' },
          ]} />
        </div>

        {/* Today's Appointments */}
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="today-appointments-list">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-700">Today's Appointments</h3>
            <span className="text-[10px] text-emerald-600 font-medium flex items-center gap-1" title="Auto-refreshes every 30s">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> LIVE
            </span>
          </div>
          {/* Item 6 — clickable status pills above the list, drives the filter below */}
          <div className="grid grid-cols-3 gap-1.5 mb-3" data-testid="doctor-queue-pills">
            <button onClick={() => setStatusFilter(statusFilter === 'waiting' ? null : 'waiting')}
              className={cn('rounded-lg py-1.5 text-center transition-all',
                statusFilter === 'waiting' ? 'bg-blue-600 text-white ring-2 ring-blue-300' : 'bg-blue-50 text-blue-700 hover:bg-blue-100')}
              data-testid="doctor-pill-waiting">
              <p className="text-base font-bold">{tCounts.waiting}</p>
              <p className="text-[9px] font-medium">Waiting</p>
            </button>
            <button onClick={() => setStatusFilter(statusFilter === 'in_progress' ? null : 'in_progress')}
              className={cn('rounded-lg py-1.5 text-center transition-all',
                statusFilter === 'in_progress' ? 'bg-purple-600 text-white ring-2 ring-purple-300' : 'bg-purple-50 text-purple-700 hover:bg-purple-100')}
              data-testid="doctor-pill-in_progress">
              <p className="text-base font-bold">{tCounts.in_progress}</p>
              <p className="text-[9px] font-medium">In Progress</p>
            </button>
            <button onClick={() => setStatusFilter(statusFilter === 'completed' ? null : 'completed')}
              className={cn('rounded-lg py-1.5 text-center transition-all',
                statusFilter === 'completed' ? 'bg-emerald-600 text-white ring-2 ring-emerald-300' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100')}
              data-testid="doctor-pill-completed">
              <p className="text-base font-bold">{tCounts.completed}</p>
              <p className="text-[9px] font-medium">Completed</p>
            </button>
          </div>
          <div className="space-y-2 max-h-[280px] overflow-y-auto">
            {filteredAppointments.length > 0 ? filteredAppointments.map((a, i) => (
              <div key={i} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold">
                    {a.patient_name?.charAt(0) || '?'}
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-700">{a.patient_name || 'Patient'}</p>
                    <p className="text-[10px] text-slate-400">{a.reason || a.type || 'Consultation'}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={cn("text-[10px] px-2 py-0.5 rounded-full font-semibold",
                    a.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                    a.status === 'checked_in' ? 'bg-blue-100 text-blue-700' :
                    a.status === 'cancelled' ? 'bg-red-100 text-red-600' :
                    'bg-slate-200 text-slate-600'
                  )}>{a.status === 'checked_in' ? 'Ongoing' : a.status}</span>
                  <p className="text-[10px] text-slate-400 mt-0.5">{a.time_slot || ''}</p>
                </div>
              </div>
            )) : (
              <p className="text-xs text-slate-400 text-center py-8">No appointments today</p>
            )}
          </div>
        </div>

        {/* Next Patient */}
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="next-patient-card">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Next Patient Details</h3>
          {next_patient ? (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center text-white font-bold">
                  {next_patient.name?.charAt(0)}
                </div>
                <div>
                  <p className="font-bold text-slate-800">{next_patient.name}</p>
                  <p className="text-[10px] text-slate-400">UHID: {next_patient.uhid || 'N/A'}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
                {next_patient.age && <div><span className="text-slate-400 block text-[10px]">Age</span><span className="font-medium">{next_patient.age}</span></div>}
                {next_patient.gender && <div><span className="text-slate-400 block text-[10px]">Gender</span><span className="font-medium capitalize">{next_patient.gender}</span></div>}
                {next_patient.blood_group && <div><span className="text-slate-400 block text-[10px]">Blood</span><span className="font-medium">{next_patient.blood_group}</span></div>}
              </div>
              <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
                {next_patient.last_visit && <div><span className="text-slate-400 block text-[10px]">Last Visit</span><span className="font-medium">{next_patient.last_visit}</span></div>}
                {next_patient.registration_date && <div><span className="text-slate-400 block text-[10px]">Reg. Date</span><span className="font-medium">{next_patient.registration_date}</span></div>}
              </div>
              <div className="flex items-center gap-2 mb-3 text-xs">
                <Clock size={12} className="text-slate-400" />
                <span className="font-medium">{next_patient.time_slot}</span>
                <span className="text-slate-400 mx-1">|</span>
                <span className="text-slate-500">{next_patient.type || 'Visit'}</span>
              </div>
              {next_patient.conditions?.length > 0 && (
                <div>
                  <p className="text-[10px] text-slate-400 uppercase font-semibold mb-1">Patient History</p>
                  <div className="flex flex-wrap gap-1">
                    {next_patient.conditions.map((c, i) => (
                      <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-rose-50 text-rose-600 border border-rose-200">{c}</span>
                    ))}
                  </div>
                </div>
              )}
              {next_patient.phone && (
                <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
                  <Phone size={12} /> {next_patient.phone}
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8">
              <UserCheck size={28} className="text-slate-300 mb-2" />
              <p className="text-xs text-slate-400">No patients in queue</p>
            </div>
          )}
        </div>
      </div>

      {/* Item 5 — "Patient Outcomes" section removed from Doctor Dashboard per product
          feedback (didn't fit). Outcomes data is still available on each patient's
          detail screen and on the admin command center. */}

      {/* Monthly Appointment Trend (full width now that Outcomes is gone) */}
      <div className="grid grid-cols-1">
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="monthly-trend">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">Appointments (Monthly)</h3>
          <div className="flex items-center gap-4 mb-3 text-[10px] text-slate-400">
            <span className="flex items-center gap-1"><span className="h-2 w-2 bg-emerald-500 rounded-full" /> Completed</span>
            <span className="flex items-center gap-1"><span className="h-2 w-2 bg-slate-300 rounded-full" /> Total</span>
          </div>
          <BarChart data={monthly_trend} />
        </div>
      </div>

      {/* Item 6 — Attendance (full-month box grid like Ward & Beds) + self-editable Weekly Schedule */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="attendance-section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-700">Attendance ({patient_summary.month} {patient_summary.year})</h3>
            <SeedAttendanceButton onSeeded={() => window.location.reload()} />
          </div>
          <AttendanceCalendar attendance={attendance || []} />
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="schedule-section">
          <WeeklyScheduleEditor schedule={schedule || {}} canEdit={true} />
        </div>
      </div>

      {/* New — Gender Distribution + Patients by Age Group pie charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="gender-distribution-card">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Gender Distribution</h3>
          <Donut data={[
            { label: 'Male', value: gender_distribution?.male || 0, color: '#3b82f6' },
            { label: 'Female', value: gender_distribution?.female || 0, color: '#ec4899' },
            { label: 'Other', value: gender_distribution?.other || 0, color: '#94a3b8' },
          ]} />
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="age-group-card">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Patients by Age Group</h3>
          <Donut data={Object.entries(age_groups || {}).map(([k, v], i) => ({
            label: k,
            value: v,
            color: ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444', '#0ea5e9'][i % 6],
          }))} />
        </div>
      </div>

      {/* Item 3 (updated) — Today's Day Calendar at the BOTTOM, with drag-and-drop reschedule */}
      <DayCalendar
        doctors={[{ user_id: data.doctor_id, name: data.name, department: data.department }]}
        appointments={today_appointments || []}
        date={new Date().toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })}
        onRescheduled={onRescheduled}
      />
    </div>
  );
};

// Small inline helper — lets the doctor seed dummy attendance for the current month
const SeedAttendanceButton = ({ onSeeded }) => {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    setBusy(true);
    try {
      const res = await doctorDashboardAPI.seedMyAttendance();
      toast.success(`Seeded ${res.data.created} attendance record(s)`);
      onSeeded?.();
    } catch { toast.error('Failed to seed attendance'); }
    setBusy(false);
  };
  return (
    <button onClick={handle} disabled={busy} className="text-[10px] text-slate-400 hover:text-emerald-600 flex items-center gap-1" data-testid="seed-attendance-btn">
      <RefreshCw size={10} className={busy ? 'animate-spin' : ''} /> {busy ? 'Seeding…' : 'Seed dummy'}
    </button>
  );
};

// ========== ADMIN VIEW (All Doctors Summary) ==========
const AdminDoctorsView = ({ data, onSelectDoctor, canViewFinance, onRescheduled }) => {
  const [selectedId, setSelectedId] = useState(null);
  const [doctorData, setDoctorData] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const handleSelect = async (uid) => {
    if (selectedId === uid) { setSelectedId(null); setDoctorData(null); return; }
    setSelectedId(uid);
    setLoadingDetail(true);
    try {
      const res = await doctorDashboardAPI.get(uid);
      setDoctorData(res.data);
    } catch { toast.error('Failed to load doctor data'); }
    setLoadingDetail(false);
  };

  const dotm = data.doctor_of_the_month;

  // Aggregate stat cards — Revenue dropped for non-finance roles to keep Front Desk / Billing focused.
  const aggStats = [
    { label: 'Available Today', value: data.available, icon: UserCheck, iconBg: 'bg-emerald-500', sub: `${data.on_leave} on leave, ${data.absent} absent` },
    { label: "Today's Appointments", value: data.aggregate.today_appointments, icon: CalendarDays, iconBg: 'bg-blue-500', sub: `${data.aggregate.today_completed} completed` },
    { label: "Month Appointments", value: data.aggregate.month_appointments, icon: Activity, iconBg: 'bg-indigo-500', sub: `${data.aggregate.month_completed} completed` },
    ...(canViewFinance ? [{ label: "Month Revenue", value: data.aggregate.month_revenue, icon: IndianRupee, iconBg: 'bg-amber-500', prefix: 'Rs.', sub: `From ${data.total_doctors} doctors` }] : []),
    { label: "Total Doctors", value: data.total_doctors, icon: Stethoscope, iconBg: 'bg-violet-500', sub: `${data.available} active today` },
    { label: "Completion Rate", value: data.aggregate.month_appointments > 0 ? Math.round((data.aggregate.month_completed / data.aggregate.month_appointments) * 100) : 0, icon: Heart, iconBg: 'bg-rose-500', suffix: '%', sub: 'This month' },
  ];

  return (
    <div className="space-y-6" data-testid="admin-doctors-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Doctor Dashboard</h1>
        <p className="text-sm text-slate-500 mt-0.5">{data.month} {data.year} &middot; {data.total_doctors} doctor{data.total_doctors !== 1 ? 's' : ''}</p>
      </div>

      {/* Top Row: Doctor of the Month + Aggregate Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
        {/* Doctor of the Month */}
        <div className="lg:col-span-1 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-6 text-white shadow-xl" data-testid="doctor-of-month">
          <p className="text-xs font-semibold uppercase tracking-wider text-blue-200 mb-4 flex items-center gap-1.5"><Award size={14} /> Doctor of the Month</p>
          {dotm ? (
            <div className="flex flex-col items-center text-center">
              <div className="h-20 w-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-3xl font-bold mb-3 ring-4 ring-white/30">
                {dotm.name?.charAt(0)}
              </div>
              <h3 className="text-lg font-bold">{dotm.name}</h3>
              <p className="text-blue-200 text-xs mb-4">{dotm.department}</p>
              <div className="flex items-center gap-6 text-center">
                <div>
                  <p className="text-2xl font-bold">{dotm.patients_checked}</p>
                  <p className="text-[10px] text-blue-200">Checked</p>
                </div>
                <div className="h-8 w-px bg-blue-400" />
                <div>
                  <p className="text-2xl font-bold">{dotm.total_patients}</p>
                  <p className="text-[10px] text-blue-200">Patients</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center py-6">
              <Star size={32} className="text-blue-300 mb-2" />
              <p className="text-sm text-blue-200">No data yet</p>
            </div>
          )}
        </div>

        {/* Aggregate Stats */}
        <div className="lg:col-span-3 grid grid-cols-2 lg:grid-cols-3 gap-4">
          {aggStats.map((c, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-all" data-testid={`agg-${c.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className={cn("h-9 w-9 rounded-lg flex items-center justify-center mb-2", c.iconBg)}>
                <c.icon size={18} className="text-white" />
              </div>
              <p className="text-xs text-slate-400 font-medium uppercase">{c.label}</p>
              <p className="text-xl font-bold text-slate-900">{c.prefix || ''}{typeof c.value === 'number' ? c.value.toLocaleString('en-IN') : c.value}{c.suffix || ''}</p>
              <p className="text-[10px] text-slate-400">{c.sub}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Doctor Grid + Detail */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className={cn(selectedId ? "lg:col-span-1" : "lg:col-span-3")}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-700">All Doctors</h3>
            {/* Legend — Front Desk can verify doctor presence at a glance */}
            <div className="flex items-center gap-3 text-[10px] text-slate-500" data-testid="doctor-status-legend">
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500 doctor-status-glow" /> In</span>
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-amber-500" /> On Leave</span>
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-slate-400" /> Absent</span>
            </div>
          </div>
          <div className={cn("grid gap-3", selectedId ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-4")}>
            {data.doctors.map(d => {
              const statusColors = { present: 'bg-emerald-500', on_leave: 'bg-amber-500', absent: 'bg-slate-400' };
              const statusLabels = { present: 'In', on_leave: 'On Leave', absent: 'Absent' };
              const isPresent = d.status === 'present';
              return (
                <div key={d.user_id}
                  onClick={() => handleSelect(d.user_id)}
                  className={cn(
                    "bg-white rounded-xl border p-4 cursor-pointer hover:shadow-md transition-all",
                    selectedId === d.user_id ? "ring-2 ring-blue-500 border-blue-300" : "border-slate-200"
                  )}
                  data-testid={`admin-doctor-card-${d.user_id}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white font-bold">
                      {d.name?.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">{d.name}</p>
                      <p className="text-[10px] text-slate-400">{d.department}</p>
                    </div>
                    <span
                      className={cn("h-3 w-3 rounded-full flex-shrink-0 ring-1 ring-white", statusColors[d.status], isPresent && "doctor-status-glow")}
                      title={statusLabels[d.status] || d.status}
                      data-testid={`doctor-status-${d.user_id}`}
                    />
                  </div>
                  <div className={cn("grid gap-2 text-center text-xs", canViewFinance ? "grid-cols-3" : "grid-cols-2")}>
                    <div className="bg-slate-50 rounded-lg p-1.5">
                      <p className="font-bold text-slate-700">{d.month_total}</p>
                      <p className="text-[8px] text-slate-400">Month</p>
                    </div>
                    <div className="bg-emerald-50 rounded-lg p-1.5">
                      <p className="font-bold text-emerald-600">{d.month_patients}</p>
                      <p className="text-[8px] text-slate-400">Patients</p>
                    </div>
                    {canViewFinance && (
                      <div className="bg-blue-50 rounded-lg p-1.5">
                        <p className="font-bold text-blue-600">Rs.{(d.month_revenue / 1000).toFixed(0)}K</p>
                        <p className="text-[8px] text-slate-400">Revenue</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {data.doctors.length === 0 && (
              <div className="col-span-full flex flex-col items-center py-12 bg-white rounded-xl border border-slate-200">
                <Stethoscope size={32} className="text-slate-300 mb-2" />
                <p className="text-sm text-slate-400">No doctors registered</p>
              </div>
            )}
          </div>
        </div>

        {/* Doctor Detail Panel */}
        {selectedId && (
          <div className="lg:col-span-2">
            {loadingDetail ? (
              <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400">Loading...</div>
            ) : doctorData ? (
              <DoctorSelfView data={doctorData} canViewFinance={canViewFinance} onRescheduled={onRescheduled} />
            ) : null}
          </div>
        )}
      </div>

      {/* Gender Distribution + Patients by Age Group pie charts (hospital-wide) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="gender-distribution-card-admin">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Gender Distribution</h3>
          <Donut data={[
            { label: 'Male', value: data.gender_distribution?.male || 0, color: '#3b82f6' },
            { label: 'Female', value: data.gender_distribution?.female || 0, color: '#ec4899' },
            { label: 'Other', value: data.gender_distribution?.other || 0, color: '#94a3b8' },
          ]} />
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="age-group-card-admin">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Patients by Age Group</h3>
          <Donut data={Object.entries(data.age_groups || {}).map(([k, v], i) => ({
            label: k,
            value: v,
            color: ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444', '#0ea5e9'][i % 6],
          }))} />
        </div>
      </div>

      {/* Item 3 (updated) — Day calendar across ALL doctors at the BOTTOM with drag-and-drop */}
      <DayCalendar
        doctors={data.doctors || []}
        appointments={data.today_appointments_all || []}
        date={new Date().toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })}
        onRescheduled={onRescheduled}
      />
    </div>
  );
};

// ========== MAIN PAGE ==========
export const DoctorDashboardPage = () => {
  const { user } = useAuth();
  const userRoleCode = (user?.role_code || user?.role_name || '').toLowerCase().replace(/\s+/g, '_');
  // Finance widgets (Month Revenue KPI + per-card Revenue stat) hidden from Front Desk & Billing
  const canViewFinance = user?.is_super_admin || ['hospital_admin', 'accountant', 'finance', 'doctor'].includes(userRoleCode);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDoctor, setIsDoctor] = useState(false);

  const load = useCallback(async () => {
    try {
      // Try loading as doctor first
      try {
        const res = await doctorDashboardAPI.me();
        setData(res.data);
        setIsDoctor(true);
        setLoading(false);
        return;
      } catch (e) {
        if (e.response?.status !== 403) throw e;
      }
      // Admin view
      const res = await doctorDashboardAPI.all();
      setData(res.data);
      setIsDoctor(false);
    } catch (e) {
      toast.error('Failed to load doctor dashboard');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    // Item 6 — auto-refresh every 30s so the doctor dashboard stays in sync with queue/appointment status changes
    const intv = setInterval(load, 30000);
    return () => clearInterval(intv);
  }, [load]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="doctor-dashboard-loading">
        <div className="h-8 bg-slate-200 rounded w-64 animate-pulse" />
        <div className="grid grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-32 bg-slate-200 rounded-xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  if (!data) return <p className="text-slate-500 text-center py-16">No data available</p>;

  return isDoctor ? <DoctorSelfView data={data} canViewFinance={canViewFinance} onRescheduled={load} /> : <AdminDoctorsView data={data} canViewFinance={canViewFinance} onRescheduled={load} />;
};

export default DoctorDashboardPage;
